import { type BarnLogAddInput, barnLogAddInputSchema } from '@bnb/contracts';
import { v7 as uuidv7 } from 'uuid';
import { errorMessage } from './utils.ts';

/**
 * Offline outbox for barn-log entries (the /crew page).
 *
 * Every entry is saved on the phone FIRST (IndexedDB via Dexie in the app, memory in tests),
 * then sent. Each entry carries a UUIDv7 `clientEntryId` made on the phone; the API stores
 * an id exactly once, so replaying an entry (retry, two tabs, a flaky barn signal) can never
 * double-count birds or feed. That is why "send again" is always safe here.
 *
 * Storage is an interface so this logic is unit-tested without IndexedDB (test/outbox.test.ts).
 */

export type OutboxStatus = 'pending' | 'held' | 'rejected';

export interface OutboxItem {
  /** = input.clientEntryId (UUIDv7) — also the idempotency key on the server. */
  id: string;
  input: BarnLogAddInput;
  /** UTC ISO time the entry was saved on the phone. */
  createdAt: string;
  attempts: number;
  lastAttemptAt: string | null;
  lastError: string | null;
  /**
   * 'held' = the API said this ACCOUNT may not save entries (FORBIDDEN — e.g. a new crew member
   * whose role the owner hasn't set yet). Kept on the phone and sent again once the account works
   * (`retryAll`, called when `me` succeeds) or when the user taps "Try again" (`retry`).
   * 'rejected' = the API said the entry itself is invalid; it won't be retried.
   */
  status: OutboxStatus;
}

export interface OutboxStorage {
  put(item: OutboxItem): Promise<void>;
  remove(id: string): Promise<void>;
  /** Any order — the outbox sorts by createdAt. */
  all(): Promise<OutboxItem[]>;
}

export function createMemoryOutboxStorage(initial: OutboxItem[] = []): OutboxStorage {
  const map = new Map(initial.map((i) => [i.id, i]));
  return {
    async put(item) {
      map.set(item.id, structuredClone(item));
    },
    async remove(id) {
      map.delete(id);
    },
    async all() {
      return [...map.values()].map((i) => structuredClone(i));
    },
  };
}

/**
 * transient = try again later (offline, 5xx, signed out); held = this account may not save entries
 * right now (fixable by the owner, so keep the entry); permanent = the entry itself is bad.
 */
export type ErrorKind = 'transient' | 'held' | 'permanent';

// Errors about the entry itself: sending the same entry again can never succeed. (barnLog.add
// doesn't return CONFLICT today — a replayed id returns the stored entry — but a conflict would be
// about this entry too.)
const PERMANENT_CODES = new Set([
  'BAD_REQUEST',
  'CONFLICT',
  'PARSE_ERROR',
  'UNPROCESSABLE_CONTENT',
]);
// Errors about the ACCOUNT (no role yet, wrong role): the owner can fix these, so never throw the
// entry away — hold it until the account works.
const HELD_CODES = new Set(['FORBIDDEN']);

/** Default classifier: duck-types tRPC errors (err.data.code) so this file has no tRPC import. */
export function classifyError(err: unknown): ErrorKind {
  if (err && typeof err === 'object' && 'data' in err) {
    const data = err.data;
    if (data && typeof data === 'object' && 'code' in data && typeof data.code === 'string') {
      if (HELD_CODES.has(data.code)) return 'held';
      return PERMANENT_CODES.has(data.code) ? 'permanent' : 'transient';
    }
  }
  return 'transient';
}

export type NewBarnLogEntry = Omit<BarnLogAddInput, 'clientEntryId'>;

export interface FlushResult {
  sent: number;
  held: number;
  rejected: number;
  remaining: number;
}

export interface OutboxSnapshot {
  items: readonly OutboxItem[];
  pending: number;
  held: number;
  rejected: number;
  flushing: boolean;
  lastSyncedAt: string | null;
  lastError: string | null;
}

export interface Outbox {
  /** Validate, stamp a UUIDv7 and persist. Does NOT send — call flush() (the sync loop does). */
  enqueue(entry: NewBarnLogEntry): Promise<OutboxItem>;
  /** Send pending items oldest-first. Stops at the first transient or held failure (keeps order). */
  flush(): Promise<FlushResult>;
  /** Drop an item (e.g. a rejected entry the user has read). */
  discard(id: string): Promise<void>;
  /** Put one held item back in the queue ("Try again"). Does NOT send — call flush(). */
  retry(id: string): Promise<void>;
  /** Put every held item back in the queue (the account works again). Returns how many. */
  retryAll(): Promise<number>;
  /** Re-read storage (another tab may have changed it). */
  refresh(): Promise<void>;
  getSnapshot(): OutboxSnapshot;
  subscribe(listener: () => void): () => void;
}

export interface OutboxOptions {
  storage: OutboxStorage;
  send: (input: BarnLogAddInput) => Promise<unknown>;
  classify?: (err: unknown) => ErrorKind;
  now?: () => Date;
  newId?: () => string;
  /** Cross-tab mutual exclusion for flushes (navigator.locks in the app). */
  withLock?: <T>(fn: () => Promise<T>) => Promise<T>;
  /** Called after a flush that delivered at least one entry (e.g. refetch the recent list). */
  onDelivered?: (count: number) => void;
  /** Called after any local change (e.g. tell other tabs via BroadcastChannel). */
  onChange?: () => void;
}

const EMPTY: OutboxSnapshot = {
  items: [],
  pending: 0,
  held: 0,
  rejected: 0,
  flushing: false,
  lastSyncedAt: null,
  lastError: null,
};

export function createOutbox(options: OutboxOptions): Outbox {
  const { storage, send } = options;
  const classify = options.classify ?? classifyError;
  const now = options.now ?? (() => new Date());
  const newId = options.newId ?? (() => uuidv7());
  const withLock = options.withLock ?? ((fn) => fn());

  let snapshot: OutboxSnapshot = EMPTY;
  let inFlight: Promise<FlushResult> | null = null;
  let flushAgain = false;
  const listeners = new Set<() => void>();

  const publish = (patch: Partial<OutboxSnapshot>) => {
    snapshot = { ...snapshot, ...patch };
    for (const l of listeners) l();
  };

  const reload = async () => {
    const items = (await storage.all()).sort((a, b) =>
      a.createdAt === b.createdAt
        ? a.id.localeCompare(b.id)
        : a.createdAt.localeCompare(b.createdAt),
    );
    publish({
      items,
      pending: items.filter((i) => i.status === 'pending').length,
      held: items.filter((i) => i.status === 'held').length,
      rejected: items.filter((i) => i.status === 'rejected').length,
    });
    return items;
  };

  const runFlush = async (): Promise<FlushResult & { stopped: boolean }> => {
    let sent = 0;
    let held = 0;
    let rejected = 0;
    let stopped = false;
    let lastError: string | null = null;
    const items = (await reload()).filter((i) => i.status === 'pending');
    for (const item of items) {
      const attemptAt = now().toISOString();
      try {
        await send(item.input);
        await storage.remove(item.id);
        sent += 1;
      } catch (err) {
        lastError = errorMessage(err);
        const failed: OutboxItem = {
          ...item,
          attempts: item.attempts + 1,
          lastAttemptAt: attemptAt,
          lastError,
        };
        const kind = classify(err);
        if (kind === 'permanent') {
          await storage.put({ ...failed, status: 'rejected' });
          rejected += 1;
          continue;
        }
        if (kind === 'held') {
          // The account can't save entries right now; later entries would fail the same way.
          await storage.put({ ...failed, status: 'held' });
          held += 1;
          stopped = true;
          break;
        }
        await storage.put(failed);
        stopped = true;
        break; // offline or server trouble: stop, keep order, try again later
      }
    }
    const remaining = (await reload()).filter((i) => i.status === 'pending').length;
    publish({
      lastError: stopped || rejected > 0 ? lastError : null,
      ...(sent > 0 ? { lastSyncedAt: now().toISOString() } : {}),
    });
    if (sent > 0) options.onDelivered?.(sent);
    if (sent > 0 || rejected > 0 || held > 0) options.onChange?.();
    return { sent, held, rejected, remaining, stopped };
  };

  /** Flush until nothing new arrived mid-flush (an entry saved while sending goes out too). */
  const flushLoop = async (): Promise<FlushResult> => {
    const total: FlushResult = { sent: 0, held: 0, rejected: 0, remaining: 0 };
    let result: Awaited<ReturnType<typeof runFlush>>;
    do {
      flushAgain = false;
      result = await withLock(runFlush);
      total.sent += result.sent;
      total.held += result.held;
      total.rejected += result.rejected;
      total.remaining = result.remaining;
    } while (flushAgain && !result.stopped);
    return total;
  };

  return {
    async enqueue(entry) {
      const input = barnLogAddInputSchema.parse({ ...entry, clientEntryId: newId() });
      const item: OutboxItem = {
        id: input.clientEntryId,
        input,
        createdAt: now().toISOString(),
        attempts: 0,
        lastAttemptAt: null,
        lastError: null,
        status: 'pending',
      };
      await storage.put(item);
      await reload();
      options.onChange?.();
      return item;
    },

    flush() {
      // Single flight per tab; withLock gives single flight across tabs.
      if (inFlight) {
        flushAgain = true;
        return inFlight;
      }
      publish({ flushing: true });
      inFlight = flushLoop().finally(() => {
        inFlight = null;
        publish({ flushing: false });
      });
      return inFlight;
    },

    async discard(id) {
      await storage.remove(id);
      await reload();
      options.onChange?.();
    },

    async retry(id) {
      const item = (await storage.all()).find((i) => i.id === id);
      if (item?.status !== 'held') return;
      await storage.put({ ...item, status: 'pending' });
      await reload();
      options.onChange?.();
    },

    async retryAll() {
      const held = (await storage.all()).filter((i) => i.status === 'held');
      if (held.length === 0) return 0;
      for (const item of held) await storage.put({ ...item, status: 'pending' });
      await reload();
      options.onChange?.();
      return held.length;
    },

    async refresh() {
      await reload();
    },

    getSnapshot: () => snapshot,

    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export interface SyncLoopOptions {
  intervalMs?: number;
  target?: Pick<Window, 'addEventListener' | 'removeEventListener'>;
  doc?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>;
  isOnline?: () => boolean;
}

/**
 * Keep the outbox draining: on start, when the phone comes back online, when the app is
 * brought to the front, and every 30 s while anything is waiting.
 */
export function startOutboxSync(outbox: Outbox, options: SyncLoopOptions = {}): () => void {
  const target = options.target ?? window;
  const doc = options.doc ?? document;
  const isOnline = options.isOnline ?? (() => navigator.onLine);
  const intervalMs = options.intervalMs ?? 30_000;

  const tryFlush = () => {
    if (!isOnline()) return;
    void outbox.flush();
  };
  const onVisible = () => {
    if (doc.visibilityState !== 'visible') return;
    void outbox.refresh().then(tryFlush);
  };

  target.addEventListener('online', tryFlush);
  doc.addEventListener('visibilitychange', onVisible);
  const timer = setInterval(() => {
    if (outbox.getSnapshot().pending > 0) tryFlush();
  }, intervalMs);
  void outbox.refresh().then(tryFlush);

  return () => {
    target.removeEventListener('online', tryFlush);
    doc.removeEventListener('visibilitychange', onVisible);
    clearInterval(timer);
  };
}

/** navigator.locks-based cross-tab lock, or a pass-through where unsupported. */
export function browserFlushLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks) return fn();
  return locks.request('bnb-outbox-flush', fn);
}
